import React, {useEffect, useRef, useState} from "react";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Toolbar,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReactQuill from "react-quill";

import 'react-quill/dist/quill.snow.css';
import {addArmatecaArticle, getArticleById, updateArmatecaArticle, uploadMedia} from "../api/armateca-articles";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {fetchArmatekaSubjects} from "../api";
import {AddArticlePayload, ArmatecaArticle, ArmatecaArticleResponse} from "../pages/ArmatecaPage/ArmatecaArticle";
import {DeltaStatic} from 'quill';
import cogoToast from 'cogo-toast';
import {UtcDateTimePicker} from "../pages/ArmatecaPage/DateTimePicker";
import {
  getAllImagesFromDelta,
  getFirstH1FromDelta,
  getFormattedDate,
  getPlainTextPreview
} from "../util/addArticleFunctions";
import {ImagePicker} from "./ImagePicker";
import {ImageModal} from "./AddImageToArticle";
import '../util/ImageWithCaptionBlot'
import CaptionModal from "./EditCaptionModal";
import {CustomToolbar, Transition} from "./QuillToolbar";

interface AddArticleProps {
  open: boolean
  handleClose: () => void
  article?: ArmatecaArticle | null
}

interface Subject {
  id: number
  image: string
  name: string
  view_count: number
}

export const ArticleEditor = ({open, handleClose, article}: AddArticleProps) => {
  const [step, setStep] = useState(1); // 0 - редактор, 1 - данные о статье
  const [articleBody, setArticleBody] = useState<DeltaStatic>({ops: []} as unknown as DeltaStatic);
  const [coverImageUrl, setCoverImageUrl] = useState<string>("");
  const [imagesFileList, setImagesFileList] = useState<string[]>([])
  const [subjectList, setSubjectList] = useState<Subject[]>([]);
  const [isDelayedPublish, setIsDelayedPublish] = useState(false)
  const [publishingTime, setPublishingTime] = useState("");
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [sources, setSources] = useState("");
  const [announce, setAnnounce] = useState("");
  const quillRef = useRef<any | null>(null)
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [activeCaptionNode, setActiveCaptionNode] = useState<HTMLElement | null>(null);
  const [currentCaption, setCurrentCaption] = useState('');
  const [captionModal, setCaptionModal] = useState(false);
  const [isUndoAvailable, setIsUndoAvailable] = useState<boolean>(false);
  const [isRedoAvailable, setIsRedoAvailable] = useState<boolean>(false);

  useEffect(() => {
    if (!article || !open) return

    handleGetExistingArticle.mutate(article.ext_id)
    // eslint-disable-next-line
  }, [article, open]);

  const handleGetExistingArticle = useMutation({
    mutationFn: getArticleById,
    onSuccess: async (data: ArmatecaArticleResponse) => {
      const subjectsByName = data.subjects.map(subject => subjectList.find(el => el.name === subject.name)!.name)
      setArticleBody(JSON.parse(data.text))
      const quill = quillRef.current?.getEditor();
      quill.setContents(JSON.parse(data.text))
      setCoverImageUrl(data.cover_image)
      setImagesFileList([data.cover_image])
      setPublishingTime(data.planned_publication_time)
      setTitle(data.title)
      setAnnounce(data.announcement)
      setSources(data.sources)
      setSelectedSubjects(subjectsByName)
    },
    onError: () => {
      cogoToast.error('Ошибка получения статьи');
      handleResetModal()
      handleClose()
    }
  });

  const handleNextStep = () => {
    const quill = quillRef.current?.getEditor();
    const article = quill.getContents()
    setArticleBody(quill.getContents())
    setStep(1)
    setIsUndoAvailable(false)
    setIsRedoAvailable(false)

    if (!coverImageUrl) {
      const images = getAllImagesFromDelta(article)
      setImagesFileList(images)
    }

    if (!title) {
      const headerH1 = getFirstH1FromDelta(article)
      setTitle(headerH1)
    }

    if (!announce) {
      const announceTemp = getPlainTextPreview(article)
      setAnnounce(announceTemp)
    }
  }

  const handlePrevStep = () => {
    setStep(0)

    setTimeout(() => {
      const quill = quillRef.current?.getEditor();
      quill.setContents(articleBody)
    }, 150)
  }

  const handleResetModal = () => {
    setStep(0)
    setArticleBody({ops: []} as unknown as DeltaStatic)
    setCoverImageUrl("")
    setPublishingTime("")
    setIsDelayedPublish(false)
    setAnnounce("")
    setTitle("")
    setSelectedSubjects([])
    setSources("")
    setImagesFileList([])
    setIsUndoAvailable(false)
    setIsRedoAvailable(false)
  }

  useEffect(() => {
    if (!open) {
      handleResetModal()
    }
  }, [open])

  const handleFetchSubjects = async () => {
    const data = await fetchArmatekaSubjects()
    setSubjectList(data)
  }

  useEffect(() => {
    handleFetchSubjects()
  }, []);

  const queryClient = useQueryClient();

  const uploadPreviewImage = useMutation({
    mutationFn: uploadMedia,
    onError: () => {
      cogoToast.error('Ошибка загрузки превью, попробуйте снова');
    }
  });

  const addArmatekaArticle = useMutation({
    mutationFn: addArmatecaArticle,
    mutationKey: ["armateka-articles"],
    onSettled: () => queryClient.invalidateQueries({queryKey: ["armateka-articles"]}),
    onSuccess: () => {
      cogoToast.success('Статья успешно добавлена');
      handleResetModal()
      handleClose()
    },
    onError: (e) => {
      cogoToast.error('Ошибка создания статьи, заполните все поля и попробуйте снова');
    }
  });

  const updateArmatekaArticleMutation = useMutation({
    mutationFn: updateArmatecaArticle,
    mutationKey: ["armateka-articles"],
    onSettled: () => queryClient.invalidateQueries({queryKey: ["armateka-articles"]}),
    onSuccess: () => {
      cogoToast.success('Статья успешно обновлена');
      handleResetModal()
      handleClose()
    },
    onError: () => {
      cogoToast.error('Ошибка обновления статьи, заполните все поля и попробуйте снова');
    }
  });

  const handleSaveArticle = async () => {
    if (!coverImageUrl) {
      cogoToast.error('Пожалуйста, выберите превью статьи');
      return
    }

    const targetSubjects = selectedSubjects.map(name => subjectList.find(el => el.name === name)!.id)

    if (!targetSubjects.length) {
      cogoToast.error('Пожалуйста, выберите тему');
      return
    }

    if (title?.length > 100) {
      cogoToast.error('Максимальная длина заголовка - 100 символов');
      return
    }

    if (announce?.length > 200) {
      cogoToast.error('Максимальная длина анонса - 200 символов');
      return
    }

    if (sources?.length > 400) {
      cogoToast.error('Максимальная длина источников - 400 символов');
      return
    }

    const payload: AddArticlePayload = {
      "subjects": targetSubjects,
      "text": JSON.stringify(articleBody.ops),
      "cover_image": coverImageUrl,
      "title": title,
      "announcement": announce,
      "status": "under_review",
      sources: sources
    }

    if (isDelayedPublish) {
      payload['planned_publication_time'] = publishingTime
    }

    if (article) {
      updateArmatekaArticleMutation.mutate({...payload, ext_id: article.ext_id, status: article.status})
    } else {
      addArmatekaArticle.mutate(payload)
    }
  }

  const handleChange = (content: any, delta: any, source: any, editor: any) => {
    if (!quillRef.current) return

    const quill = quillRef.current?.getEditor();

    const selection = quill.getSelection();
    const [line] = quill.getLine(selection?.index || 0);
    const lineIndex = quill.getIndex(line);

    const ops = delta?.ops || [];

    for (const op of ops) {
      if (op.attributes?.header === 1 && lineIndex !== 0) {
        quill.format('header', 2);
      }
    }

    const isRedo = quill?.history?.stack?.redo?.length > 0
    const isUndo = quill?.history?.stack?.undo?.length > 0
    setIsRedoAvailable(isRedo)
    setIsUndoAvailable(isUndo)
  };

  const handleUndo = () => {
    const quill = quillRef.current?.getEditor();
    if (quill) quill.history.undo();
  };

  const handleRedo = () => {
    const quill = quillRef.current?.getEditor();
    if (quill) quill.history.redo();
  };


  const insertImageWithCaption = (data: { source: string; caption?: string }) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    try {
      const range = quill.getSelection(true);
      quill.insertEmbed(range.index, 'image', data, 'user');
      quill.setSelection(range.index + 1);
    } catch (e) {
      console.error(e)
    }
  };

  const handleCaptionClick = (e: any) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('caption')) {
      e.preventDefault();

      setActiveCaptionNode(target);
      setCurrentCaption(target.innerText);
      setCaptionModal(true);
    }
  }

  const handleSaveCaption = (newCaption: string) => {
    if (activeCaptionNode) {
      activeCaptionNode.innerText = newCaption;
      const figure = activeCaptionNode.closest('figure');
      if (figure) {

        figure.setAttribute(
          'data-value',
          JSON.stringify({
            source: figure.getAttribute('data-source'),
            caption: newCaption,
          })
        );
        figure.setAttribute(
          'data-caption',
          newCaption
        );
      }
    }
    setCaptionModal(false);
  };


  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{position: 'relative', backgroundColor: 'transparent', color: "inherit", boxShadow: 'none'}}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon/>
          </IconButton>
          <Typography sx={{ml: 2, flex: 1}} variant="h6" component="div">
            {article ? `Редактирование статьи ${article.title}` : 'Добавление статьи'}
          </Typography>
          <div style={{gap: 10, display: "flex"}}>
            {step === 1 && <Button color="inherit" variant="outlined" onClick={handlePrevStep}>
                Назад
            </Button>}
            {step === 0 && <Button color="inherit" variant="outlined" onClick={handleNextStep}>
                Далее
            </Button>}
          </div>
        </Toolbar>
      </AppBar>
      <Transition in={step === 0}>
        <Box sx={{width: '1000px', marginLeft: 'auto', marginRight: "auto", paddingTop: 8}}>
          <ImageModal
            isOpen={isImageModalOpen}
            onClose={() => setIsImageModalOpen(false)}
            onSuccess={(data) => {
              insertImageWithCaption(data);
            }}
            uploadImageApi={uploadPreviewImage.mutateAsync}
          />
          <CaptionModal
            open={captionModal}
            initialCaption={currentCaption}
            onSave={handleSaveCaption}
            onClose={() => setCaptionModal(false)}
          />
          <CustomToolbar
            isUndoAvailable={isUndoAvailable}
            isRedoAvailable={isRedoAvailable}
            onUndo={handleUndo} onRedo={handleRedo} openAddImage={() => setIsImageModalOpen(true)}/>
          <div onClick={handleCaptionClick}>
            {step === 0 &&
                <ReactQuill
                    ref={quillRef}
                    modules={{
                      toolbar: {
                        container: '#toolbar',
                      },
                      history: {
                        delay: 1000,
                        maxStack: 500,
                        userOnly: true,
                      },
                    }}
                    formats={[
                      'header',
                      'bold', 'italic', 'underline', 'blockquote',
                      'list', 'bullet', 'indent',
                      'link', 'image', 'clean',
                    ]}
                    theme="snow"
                    onChange={handleChange}
                />}
          </div>
        </Box>
      </Transition>

      <Transition in={step === 1}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          marginLeft: "auto",
          marginRight: "auto",
          justifyContent: "flex-start",
          width: "80%",
          gap: 12
        }}>
          <Typography variant="h6">
            Настройки публикации
          </Typography>
          <TextField
            margin="dense"
            label="Заголовок"
            fullWidth
            variant="outlined"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Анонс"
            fullWidth
            variant="outlined"
            value={announce}
            onChange={(e) => setAnnounce(e.target.value)}
            rows={4}
            multiline
          />
          <FormControl
            style={{marginBottom: "10px"}}
            fullWidth
          >
            <InputLabel>
              Категории
            </InputLabel>
            <Select
              value={selectedSubjects}
              onChange={(e) => setSelectedSubjects(e.target.value as string[])}
              placeholder="Категории"
              multiple={true}
            >
              {subjectList.map(el => el.name).map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ImagePicker
            imageList={imagesFileList}
            setImageList={setImagesFileList}
            setSelectedUrl={setCoverImageUrl}
            selectedUrl={coverImageUrl}
            handleUploadImage={uploadPreviewImage.mutateAsync}
          />
          <TextField
            margin="dense"
            label="Источники"
            fullWidth
            variant="outlined"
            value={sources}
            onChange={(e) => setSources(e.target.value)}
          />

          <FormControlLabel
            control={<Switch/>}
            label='Опубликовать позже'
            value={isDelayedPublish}
            onChange={(_, value) => {
              setIsDelayedPublish(value);
            }}
          />

          {isDelayedPublish &&
              <UtcDateTimePicker value={publishingTime} label="Дата и время публикации" onChange={setPublishingTime}/>}
          {publishingTime && <Typography>
            Планируемая дата публикации: {getFormattedDate(publishingTime)}
          </Typography>}
          <Box mb={5} width="100%">
            <Button
              component="label"
              variant="contained"
              onClick={handleSaveArticle}
              fullWidth
            >
              Сохранить
            </Button>
          </Box>
        </div>
      </Transition>
    </Dialog>
  )
}